import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Users, GraduationCap, Sparkles, Trash2, Download, Upload,
  AlertTriangle, Info, ChevronRight, FileText, Pencil, Eraser, Share2, BookPlus,
  MoreHorizontal, Send, XCircle, RefreshCw, Star, Mail,
} from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { NationalitySelect } from "@/components/app/counsellors/NationalitySelect";
import { InsightsShareDialog } from "@/components/app/counsellors/InsightsShareDialog";
import { ManualAddCourseDialog } from "@/components/app/counsellors/ManualAddCourseDialog";
import { CoursePreviewHover } from "@/components/app/counsellors/CoursePreviewHover";
import CohortInsightsPanel from "@/components/app/counsellors/CohortInsightsPanel";
import DeadlineTrackerPanel from "@/components/app/counsellors/DeadlineTrackerPanel";
import DigestSettingsCard from "@/components/app/counsellors/DigestSettingsCard";
import SchoolAccessPanel from "@/components/app/counsellors/SchoolAccessPanel";


const REALISM_STYLE = {
  safe:    { chip: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-600", label: "Safe" },
  match:   { chip: "bg-amber-50 text-amber-800 border-amber-200",       dot: "bg-amber-500",   label: "Match" },
  reach:   { chip: "bg-rose-50 text-rose-800 border-rose-200",          dot: "bg-rose-600",    label: "Reach" },
  unknown: { chip: "bg-stone-50 text-stone-500 border-stone-200",       dot: "bg-stone-400",   label: "—" },
};


// ---------------------------------------------------------------------------
// Self-declare gate — first-time counsellors see this before the dashboard.
// ---------------------------------------------------------------------------


function SelfDeclareCard({ onDone }) {
  const [school, setSchool] = useState("");
  const [role, setRole] = useState("Counsellor");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!school.trim()) return;
    setSaving(true);
    try {
      await api.post("/counsellors/self-declare", { school_name: school.trim(), role });
      toast.success("You're a counsellor now — welcome.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto mt-10 border border-stone-300 rounded-lg bg-white p-8" data-testid="counsellors-self-declare">
      <div className="label-uppercase text-stone-500">One-time step</div>
      <h1 className="font-editorial text-3xl mt-2">Turn on Counsellor Mode</h1>
      <p className="text-sm text-stone-600 mt-2">
        We use this for cohort segmentation only — never public. You can update the school name anytime.
      </p>
      <div className="mt-6 space-y-3">
        <div>
          <label className="label-uppercase !text-[10px] text-stone-500">School / organisation</label>
          <Input
            data-testid="self-declare-school"
            value={school}
            onChange={(e) => setSchool(e.target.value)}
            placeholder="e.g. Harrow School, Kaplan Consulting"
            className="mt-1"
          />
        </div>
        <div>
          <label className="label-uppercase !text-[10px] text-stone-500">Role</label>
          <Input
            data-testid="self-declare-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-1"
          />
        </div>
      </div>
      <Button
        onClick={submit}
        disabled={!school.trim() || saving}
        data-testid="self-declare-submit"
        className="mt-6 w-full bg-stone-900 hover:bg-stone-800 text-white"
      >
        {saving ? "Saving…" : "Enable Counsellor Mode"}
      </Button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Cohort side rail
// ---------------------------------------------------------------------------


function CohortRail({ cohorts, activeId, onSelect, onCreate, onDelete, cap, used }) {
  const [newName, setNewName] = useState("");
  const [open, setOpen] = useState(false);

  return (
    <aside className="w-64 shrink-0 border-r border-stone-200 h-full py-6 pr-4" data-testid="cohort-rail">
      <div className="flex items-baseline justify-between">
        <div className="label-uppercase text-stone-500">Cohorts</div>
        <span className="text-[10px] font-mono text-stone-500">{used}/{cap}</span>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid="cohort-create-open"
            className="w-full mt-3 justify-start text-xs"
          >
            <Plus className="w-3 h-3 mr-1" /> New cohort
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New cohort</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="cohort-create-name"
              placeholder="e.g. Year 13 · 2026-27"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              data-testid="cohort-create-submit"
              disabled={!newName.trim()}
              onClick={async () => {
                await onCreate(newName.trim());
                setNewName("");
                setOpen(false);
              }}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-4 space-y-1" data-testid="cohort-list">
        {cohorts.length === 0 && (
          <div className="text-xs text-stone-500 italic mt-4 leading-relaxed">
            No cohorts yet. Start with one for each intake.
          </div>
        )}
        {cohorts.map((c) => (
          <button
            key={c.id}
            data-testid={`cohort-item-${c.id}`}
            onClick={() => onSelect(c.id)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between gap-2 group ${
              activeId === c.id ? "bg-stone-900 text-white" : "hover:bg-stone-100"
            }`}
          >
            <div className="min-w-0">
              <div className="truncate">{c.name}</div>
              <div className={`text-[10px] font-mono ${activeId === c.id ? "text-stone-300" : "text-stone-500"}`}>
                {c.academic_year} · {c.student_count} student{c.student_count === 1 ? "" : "s"}
              </div>
            </div>
            <Trash2
              className={`w-3.5 h-3.5 opacity-0 group-hover:opacity-100 ${
                activeId === c.id ? "text-white" : "text-stone-400 hover:text-rose-600"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`Delete cohort "${c.name}" and all its students?`)) onDelete(c.id);
              }}
            />
          </button>
        ))}
      </div>
    </aside>
  );
}


// ---------------------------------------------------------------------------
// Add-student inline row
// ---------------------------------------------------------------------------


function StudentDialog({ cohortId, mode = "add", initial = null, onDone, atCap = false, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", predicted_curriculum: "a_level", predicted_grades: "",
    subjects: "", passport: "", target_countries: "", budget_usd: "",
    career_interest: "", notes: "",
  });

  // Prefill on open when editing.
  useEffect(() => {
    if (open && mode === "edit" && initial) {
      setForm({
        name: initial.name || "",
        predicted_curriculum: initial.predicted_curriculum || "a_level",
        predicted_grades: initial.predicted_grades || "",
        subjects: (initial.subjects || []).join(", "),
        passport: initial.passport || "",
        target_countries: (initial.target_countries || []).join(", "),
        budget_usd: initial.budget_usd ? String(initial.budget_usd) : "",
        career_interest: initial.career_interest || "",
        notes: initial.notes || "",
        email: initial.email || "",
      });
    } else if (open && mode === "add") {
      // Reset for add
      setForm({
        name: "", predicted_curriculum: "a_level", predicted_grades: "",
        subjects: "", passport: "", target_countries: "", budget_usd: "",
        career_interest: "", notes: "", email: "",
      });
    }
  }, [open, mode, initial]);

  const submit = async () => {
    if (!form.name.trim()) return;
    const payload = {
      name: form.name.trim(),
      predicted_curriculum: form.predicted_curriculum,
      predicted_grades: form.predicted_grades.trim(),
      subjects: form.subjects.split(",").map((s) => s.trim()).filter(Boolean),
      passport: form.passport || null,
      target_countries: form.target_countries.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
      budget_usd: form.budget_usd ? parseInt(form.budget_usd, 10) : null,
      career_interest: form.career_interest,
      notes: form.notes,
      email: (form.email || "").trim() || null,
    };
    try {
      if (mode === "edit") {
        const r = await api.patch(`/counsellors/cohorts/${cohortId}/students/${initial.id}`, payload);
        toast.success(
          r.data?.wiped_auto_shortlist_rows
            ? `Updated ${payload.name} — old matches cleared, regenerate to refresh`
            : `Updated ${payload.name}`,
        );
      } else {
        await api.post(`/counsellors/cohorts/${cohortId}/students`, payload);
        toast.success(`Added ${payload.name}`);
      }
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit" ? `Edit ${initial?.name || "student"}` : "Add a student to this cohort"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div>
            <label className="label-uppercase !text-[10px] text-stone-500">Name *</label>
            <Input data-testid="student-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          </div>
          <div>
            <label className="label-uppercase !text-[10px] text-stone-500">
              Student email (optional — required to send a Fast Match invite)
            </label>
            <Input
              data-testid="student-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="student@school.edu"
              autoComplete="off"
            />
            <p className="mt-1 text-[11px] text-stone-500 italic">
              Stored on the roster row only. Never shared publicly.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase !text-[10px] text-stone-500">Curriculum</label>
              <Select value={form.predicted_curriculum} onValueChange={(v) => setForm({ ...form, predicted_curriculum: v })}>
                <SelectTrigger data-testid="student-curriculum"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="a_level">A Levels</SelectItem>
                  <SelectItem value="ib">IB Diploma</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-uppercase !text-[10px] text-stone-500">
                {form.predicted_curriculum === "ib" ? "IB total" : "Predicted grades"}
              </label>
              <Input
                data-testid="student-grades"
                value={form.predicted_grades}
                onChange={(e) => setForm({ ...form, predicted_grades: e.target.value })}
                placeholder={form.predicted_curriculum === "ib" ? "34" : "AAB"}
              />
            </div>
          </div>
          <div>
            <label className="label-uppercase !text-[10px] text-stone-500">Subjects of interest (comma-separated)</label>
            <Input
              data-testid="student-subjects"
              value={form.subjects}
              onChange={(e) => setForm({ ...form, subjects: e.target.value })}
              placeholder="e.g. Computer Science, Mathematics"
            />
            <p className="mt-1 text-[11px] text-stone-500 italic">
              Sharpens the shortlist significantly — leave blank only if the student is undecided.
            </p>
          </div>
          <div>
            <label className="label-uppercase !text-[10px] text-stone-500">Nationality</label>
            <NationalitySelect
              value={form.passport}
              onChange={(code) => setForm({ ...form, passport: code })}
              testId="student-passport"
            />
            <p className="mt-1 text-[11px] text-stone-500 italic">
              Sets the fee band (home vs international) and unlocks passport-specific scholarships.
            </p>
          </div>
          <div>
            <label className="label-uppercase !text-[10px] text-stone-500">Target countries (2-letter, csv)</label>
            <Input
              data-testid="student-countries"
              value={form.target_countries}
              onChange={(e) => setForm({ ...form, target_countries: e.target.value })}
              placeholder="UK, US, CA"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase !text-[10px] text-stone-500">Annual budget (USD)</label>
              <Input
                data-testid="student-budget"
                type="number"
                value={form.budget_usd}
                onChange={(e) => setForm({ ...form, budget_usd: e.target.value })}
                placeholder="45000"
              />
            </div>
            <div>
              <label className="label-uppercase !text-[10px] text-stone-500">Career interest</label>
              <Input
                data-testid="student-career"
                value={form.career_interest}
                onChange={(e) => setForm({ ...form, career_interest: e.target.value })}
                placeholder="e.g. Investment banking"
              />
            </div>
          </div>
          <div>
            <label className="label-uppercase !text-[10px] text-stone-500">Notes</label>
            <Textarea
              data-testid="student-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          {mode === "edit" && (
            <p className="text-[11px] text-stone-500 italic">
              Editing wipes the auto-generated shortlist so matches don't go stale against the new profile.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="student-save-submit" onClick={submit}>
            {mode === "edit" ? "Save changes" : "Add student"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Backwards-compatible wrapper — the old "Add student" button in the
// cohort header still uses AddStudentDialog by name.
function AddStudentDialog({ cohortId, onAdded, atCap }) {
  return (
    <StudentDialog
      cohortId={cohortId}
      mode="add"
      onDone={onAdded}
      atCap={atCap}
      trigger={(
        <Button
          disabled={atCap}
          data-testid="student-add-open"
          className="bg-stone-900 hover:bg-stone-800 text-white text-xs"
        >
          <Plus className="w-3 h-3 mr-1" /> Add student
        </Button>
      )}
    />
  );
}


/**
 * Bulk-import dialog: download template CSV → paste filled CSV back →
 * preview parse (backend dry-run) → commit. Two-step to catch typos
 * before we push malformed rows into Mongo.
 */
function BulkImportDialog({ cohortId, onImported }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadXlsx = () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}/api/counsellors/students/xlsx-template`, "_blank");
  };
  const downloadCsv = () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}/api/counsellors/students/csv-template`, "_blank");
  };

  const readFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(f);
  };

  const runPreview = async () => {
    if (!csv.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/counsellors/cohorts/${cohortId}/students/bulk-preview`, { csv_text: csv });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/counsellors/cohorts/${cohortId}/students/bulk-import`, { csv_text: csv });
      toast.success(`Imported ${r.data.inserted} students${r.data.skipped_over_cap ? ` (${r.data.skipped_over_cap} skipped over free-tier cap)` : ""}`);
      onImported();
      setOpen(false);
      setCsv("");
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="cohort-bulk-import-open">
          <Upload className="w-3.5 h-3.5 mr-1" /> Bulk import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk import students from CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="rounded-md border border-stone-200 bg-[#F7F5F0] p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-4 h-4 text-stone-600 shrink-0 mt-0.5" />
              <div className="text-sm text-stone-700 flex-1">
                <div className="font-semibold">Two-step import</div>
                <p className="text-xs text-stone-600 mt-1">
                  Grab a template, fill it in a spreadsheet, then paste or upload. The Excel template has
                  <span className="font-semibold"> dropdowns</span> for passport and curriculum so you can't
                  fat-finger a wrong code.
                </p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <Button variant="outline" size="sm" onClick={downloadXlsx} data-testid="bulk-import-template-xlsx">
                  <Download className="w-3.5 h-3.5 mr-1" /> Excel template <span className="ml-1 text-[9px] font-mono opacity-60">(with dropdowns)</span>
                </Button>
                <Button variant="ghost" size="sm" onClick={downloadCsv} data-testid="bulk-import-template-csv" className="text-xs">
                  <Download className="w-3.5 h-3.5 mr-1" /> CSV template <span className="ml-1 text-[9px] opacity-60">(plain)</span>
                </Button>
              </div>
            </div>
          </div>

          <div>
            <label className="label-uppercase !text-[10px] text-stone-500">Upload CSV</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={readFile}
              data-testid="bulk-import-file"
              className="mt-1 block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:bg-stone-900 file:text-white hover:file:bg-stone-800"
            />
            <p className="mt-2 text-[11px] text-stone-500 italic">
              Filled in Excel? Export as CSV (File → Save As → CSV) then upload here, or paste the CSV below.
            </p>
          </div>

          <div>
            <label className="label-uppercase !text-[10px] text-stone-500">CSV content</label>
            <Textarea
              rows={7}
              value={csv}
              onChange={(e) => { setCsv(e.target.value); setPreview(null); }}
              placeholder="name,predicted_curriculum,predicted_grades,subjects,passport,target_countries,budget_usd,career_interest,notes"
              data-testid="bulk-import-csv"
              className="font-mono text-xs"
            />
          </div>

          {preview && (
            <div className="rounded-md border border-stone-200 bg-white p-4 space-y-2" data-testid="bulk-import-preview">
              <div className="text-sm">
                <span className="font-semibold">{preview.parsed_count}</span> rows parsed ·
                <span className="text-emerald-700 ml-2">{preview.will_import} will import</span>
                {preview.would_exceed_cap > 0 && (
                  <span className="text-amber-700 ml-2">
                    · {preview.would_exceed_cap} skipped (free tier cap {preview.cap})
                  </span>
                )}
              </div>
              {preview.errors?.length > 0 && (
                <div className="text-xs text-rose-700">
                  <div className="font-semibold">Issues:</div>
                  <ul className="mt-1 space-y-0.5 list-disc list-inside">
                    {preview.errors.slice(0, 6).map((e, i) => <li key={i}>{e}</li>)}
                    {preview.errors.length > 6 && <li>… and {preview.errors.length - 6} more</li>}
                  </ul>
                </div>
              )}
              {preview.preview?.length > 0 && (
                <div className="text-xs text-stone-600">
                  Preview: <span className="font-mono">{preview.preview.map((r) => r.name).slice(0, 5).join(", ")}</span>
                  {preview.preview.length > 5 && ` …+${preview.preview.length - 5}`}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          {!preview ? (
            <Button
              disabled={!csv.trim() || busy}
              onClick={runPreview}
              data-testid="bulk-import-preview-btn"
            >
              Preview
            </Button>
          ) : (
            <Button
              disabled={busy || (preview?.will_import ?? 0) === 0}
              onClick={commit}
              data-testid="bulk-import-commit"
              className="bg-stone-900 hover:bg-stone-800 text-white"
            >
              Import {preview.will_import} students
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Cohort detail (right panel)
// ---------------------------------------------------------------------------


// FastMatchPill — at-a-glance status of a student's Fast Match invite.
// `invite` is `null` (never invited) or the counsellor-safe projection
// returned by GET /counsellors/cohorts/{cid}/invite-fast-match:
//   { status, invited_at, completed_at, cancelled_at, expires_at,
//     resend_count, last_resent_at }
function FastMatchPill({ invite, onViewShared, studentId }) {
  if (!invite || !invite.status) return null;

  // "Awaiting" = invited > 3 days ago and still not completed.
  const AWAITING_AFTER_MS = 3 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const invitedTs = invite.invited_at ? Date.parse(invite.invited_at) : null;
  const awaiting =
    invite.status === "invited" &&
    invitedTs != null &&
    now - invitedTs > AWAITING_AFTER_MS;

  const meta = (() => {
    switch (invite.status) {
      case "invited":
        return awaiting
          ? {
              label: "Awaiting",
              cls: "bg-orange-50 text-orange-800 border-orange-200",
              icon: "⏳",
              hint: `Invited ${_fmtRel(invite.invited_at)} — still no completion. Consider resending.`,
            }
          : {
              label: "Invited",
              cls: "bg-amber-50 text-amber-800 border-amber-200",
              icon: "✉",
              hint: `Invited ${_fmtRel(invite.invited_at)}${invite.resend_count ? ` · resent ${invite.resend_count}×` : ""}`,
            };
      case "completed": {
        // Step 2: split completed into three sub-states based on the
        // invite's share status. All three carry the completion date;
        // only `results_shared` exposes the view button.
        const shareStatus = invite.share_status || "not_shared";
        if (shareStatus === "shared") {
          return {
            label: "Results shared",
            cls: "bg-emerald-100 text-emerald-900 border-emerald-300",
            icon: "✓",
            hint: `Completed ${_fmtRel(invite.completed_at)} · shared ${_fmtRel(invite.share_consented_at)}`,
            action: "view_shared",
          };
        }
        if (shareStatus === "withdrawn") {
          return {
            label: "Sharing withdrawn",
            cls: "bg-stone-100 text-stone-600 border-stone-300",
            icon: null,
            hint: `Completed ${_fmtRel(invite.completed_at)} · sharing stopped ${_fmtRel(invite.share_withdrawn_at)}`,
          };
        }
        return {
          label: "Completed — private",
          cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
          icon: null,
          hint: `Completed ${_fmtRel(invite.completed_at)} — student has not shared results`,
        };
      }
      case "expired":
        return {
          label: "Expired",
          cls: "bg-stone-100 text-stone-600 border-stone-300",
          icon: null,
          hint: `Expired ${invite.expires_at ? _fmtRel(invite.expires_at) : ""}`,
        };
      case "cancelled":
        return {
          label: "Cancelled",
          cls: "bg-stone-100 text-stone-600 border-stone-300",
          icon: null,
          hint: `Cancelled ${invite.cancelled_at ? _fmtRel(invite.cancelled_at) : ""}`,
        };
      case "delivery_failed":
        return {
          label: "Not delivered",
          cls: "bg-rose-50 text-rose-800 border-rose-200",
          icon: "!",
          hint: "Email bounced or was rejected. Fix student's email address and resend.",
        };
      default:
        return null;
    }
  })();
  if (!meta) return null;
  const testid = `fm-pill-${invite.status}${awaiting ? "-awaiting" : ""}${meta.action === "view_shared" ? "-shared" : ""}`;
  if (meta.action === "view_shared") {
    return (
      <button
        type="button"
        onClick={() => onViewShared?.(studentId)}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-underground-sc tracking-wider rounded border cursor-pointer hover:brightness-95 ${meta.cls}`}
        title={`${meta.hint} — click to view shared results`}
        data-testid={testid}
      >
        {meta.icon && <span className="leading-none">{meta.icon}</span>}
        {meta.label}
      </button>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-underground-sc tracking-wider rounded border ${meta.cls}`}
      title={meta.hint}
      data-testid={testid}
    >
      {meta.icon && <span className="leading-none">{meta.icon}</span>}
      {meta.label}
    </span>
  );
}

function SharedResultModal({ info, busy, onClose }) {
  const payload = info?.payload;
  const snap = payload?.snapshot;
  return (
    <div
      className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4 print:static print:bg-transparent"
      role="dialog"
      aria-modal="true"
      data-testid="shared-result-modal"
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto print:max-w-full print:rounded-none print:shadow-none">
        <div className="flex items-start justify-between gap-3 border-b border-stone-200 px-6 py-4 print:hidden">
          <div>
            <h2 className="text-lg font-semibold text-stone-900">
              {info.name}'s shared Fast Match results
            </h2>
            <p className="text-xs text-stone-500 mt-0.5">
              Read-only — the student chose to share this page with you. Questionnaire answers are not shared.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="text-xs rounded border border-stone-300 px-3 py-1 hover:bg-stone-50"
              data-testid="shared-result-print"
            >
              Print / Save as PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-xs rounded border border-stone-300 px-3 py-1 hover:bg-stone-50"
              data-testid="shared-result-close"
            >
              Close
            </button>
          </div>
        </div>
        <div className="px-6 py-5">
          {busy && <p className="text-sm text-stone-500">Loading shared results…</p>}
          {!busy && !snap && (
            <p className="text-sm text-stone-500">
              Nothing to show — the student may have stopped sharing.
            </p>
          )}
          {!busy && snap && (
            <>
              <div className="text-xs text-stone-500 mb-4">
                Completed {_fmtRel(payload.completed_at)} · shared {_fmtRel(payload.consented_at)}
              </div>
              <ul className="space-y-4">
                {(snap.top5 || []).map((r) => (
                  <li
                    key={r.subjectId || r.rank}
                    className="rounded-xl border border-stone-200 p-4"
                    data-testid={`shared-result-row-${r.rank}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <div className="text-xs text-stone-500 font-underground-sc tracking-wider">
                          #{r.rank} · {r.family}
                        </div>
                        <h3 className="text-base font-semibold text-stone-900 mt-0.5">
                          {r.canonicalSubject}
                        </h3>
                      </div>
                      <div className="text-xs text-stone-400 font-mono">
                        {r.label}
                      </div>
                    </div>
                    {r.summary && (
                      <p className="mt-2 text-sm text-stone-700 leading-relaxed">
                        {r.summary}
                      </p>
                    )}
                    {r.strengths?.length ? (
                      <div className="mt-3">
                        <div className="text-[10px] font-underground-sc text-stone-500">Strengths</div>
                        <ul className="text-sm text-stone-700 list-disc pl-5 mt-1">
                          {r.strengths.map((x, i) => (
                            <li key={x?.dimensionCode || i}>
                              {typeof x === "string" ? x : (x?.sentence || "")}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {r.considerations?.length ? (
                      <div className="mt-3">
                        <div className="text-[10px] font-underground-sc text-stone-500">Considerations</div>
                        <ul className="text-sm text-stone-700 list-disc pl-5 mt-1">
                          {r.considerations.map((x, i) => (
                            <li key={x?.dimensionCode || i}>
                              {typeof x === "string" ? x : (x?.sentence || "")}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
              {(snap.relatedFamilies || []).length > 0 && (
                <div className="mt-6 text-xs text-stone-500">
                  Related families: {snap.relatedFamilies.join(", ")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function _fmtRel(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const abs = Math.abs(diff);
  const day = 24 * 60 * 60 * 1000;
  if (abs < 60 * 1000) return "just now";
  if (abs < 60 * 60 * 1000) {
    const m = Math.round(abs / 60000);
    return `${m} min${m === 1 ? "" : "s"} ${diff >= 0 ? "ago" : "from now"}`;
  }
  if (abs < day) {
    const h = Math.round(abs / (60 * 60 * 1000));
    return `${h} hr${h === 1 ? "" : "s"} ${diff >= 0 ? "ago" : "from now"}`;
  }
  const d = Math.round(abs / day);
  return `${d} day${d === 1 ? "" : "s"} ${diff >= 0 ? "ago" : "from now"}`;
}


function CohortDetail({ cohortId, onStudentsChanged, onCsvExport }) {
  const [cohort, setCohort] = useState(null);
  const [students, setStudents] = useState([]);
  const [comparison, setComparison] = useState({ results: [] });
  const [clicks, setClicks] = useState({});  // { student_id: { degree_id: {count, last} } }
  const [busy, setBusy] = useState(false);
  // Fast Match invitations rollup — { student_id: { status, invited_at, ... } }.
  // Loaded alongside the cohort so we can badge every row without
  // per-row round-trips. Only the counsellor's OWN invites appear here.
  const [inviteStatus, setInviteStatus] = useState({});
  const [sortByEngagement, setSortByEngagement] = useState(false);
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem("counsellor_density") || "comfortable"; }
    catch { return "comfortable"; }
  });
  const compact = density === "compact";
  const cellCls = compact ? "px-2 py-1.5" : "px-4 py-3";
  const cellClsFirst = compact ? "px-2 py-1.5 border-r border-stone-100 align-top" : "px-4 py-3 border-r border-stone-100 align-top";
  const rankCls = compact ? "px-1.5 py-1.5 font-mono text-[10px] text-stone-500" : "px-4 py-3 font-mono text-xs text-stone-500";
  const reasonLimit = compact ? 1 : 3;

  const toggleDensity = () => {
    const next = compact ? "comfortable" : "compact";
    setDensity(next);
    try { localStorage.setItem("counsellor_density", next); } catch { /* noop */ }
  };

  /** Total clicks a student has made across their entire shortlist. */
  const studentTotalClicks = (sid) => {
    const perDegree = clicks?.[sid] || {};
    return Object.values(perDegree).reduce((acc, v) => acc + (v?.count || 0), 0);
  };

  /** Picks re-ordered by (click count desc, original order preserved). */
  const sortPicksByEngagement = (sid, picks) => {
    const perDegree = clicks?.[sid] || {};
    return picks
      .map((p, idx) => ({ p, idx, n: perDegree[p.degree_id]?.count || 0 }))
      .sort((a, b) => b.n - a.n || a.idx - b.idx)
      .map((x) => x.p);
  };

  /** Students re-ordered by total engagement desc when toggle is on. */
  const displayedStudents = sortByEngagement
    ? [...students].sort((a, b) => studentTotalClicks(b.id) - studentTotalClicks(a.id))
    : students;

  const load = async () => {
    if (!cohortId) return;
    const [d, cmp, ck, inv] = await Promise.all([
      api.get(`/counsellors/cohorts/${cohortId}`),
      api.get(`/counsellors/cohorts/${cohortId}/comparison`),
      api.get(`/counsellors/cohorts/${cohortId}/clicks`).catch(() => ({ data: { clicks_by_student: {} } })),
      api.get(`/counsellors/cohorts/${cohortId}/invite-fast-match`).catch(() => ({ data: { by_student: {} } })),
    ]);
    setCohort(d.data.cohort);
    setStudents(d.data.students || []);
    setComparison(cmp.data || { results: [] });
    setClicks(ck.data?.clicks_by_student || {});
    setInviteStatus(inv.data?.by_student || {});
  };

  useEffect(() => { load(); }, [cohortId]);

  // ── Fast Match invite actions ──────────────────────────────────
  // All mutations set the two headers our backend CSRF check
  // requires: `X-Requested-With: XMLHttpRequest` (which axios sends
  // by default via api.js `withCredentials` config) and the browser
  // sends `Origin` automatically for same-origin fetches.
  const inviteHeaders = { headers: { "X-Requested-With": "XMLHttpRequest" } };

  // Fast Match Step 2 — read-only viewer for a student's shared result.
  const [sharedResult, setSharedResult] = useState(null);   // {studentId, name, payload}
  const [sharedBusy, setSharedBusy] = useState(false);
  const openSharedResult = async (studentId) => {
    const student = (students || []).find((s) => s.id === studentId);
    setSharedBusy(true);
    setSharedResult({ studentId, name: student?.name || "Student", payload: null });
    try {
      const r = await api.get(
        `/counsellors/cohorts/${cohortId}/students/${studentId}/fast-match-result`,
      );
      setSharedResult({ studentId, name: student?.name || "Student", payload: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "This student has not shared their result.");
      setSharedResult(null);
    } finally {
      setSharedBusy(false);
    }
  };

  // Toggle the star on a shortlist row. Optimistically updates the
  // comparison state so the icon flips instantly; rolls back if the
  // server rejects.
  const toggleStar = async (studentId, pick) => {
    if (!pick?.id) {
      toast.error("This pick can't be starred (missing row id)");
      return;
    }
    const nextVal = !pick.starred;
    setComparison((prev) => ({
      ...prev,
      results: (prev.results || []).map((r) =>
        r.student_id !== studentId ? r : ({
          ...r,
          picks: (r.picks || []).map((pp) =>
            pp.id === pick.id ? { ...pp, starred: nextVal } : pp
          ),
        })
      ),
    }));
    try {
      await api.patch(
        `/counsellors/cohorts/${cohortId}/students/${studentId}/shortlist/${pick.id}/star`,
        {}, inviteHeaders,
      );
    } catch (e) {
      // Roll back on error.
      setComparison((prev) => ({
        ...prev,
        results: (prev.results || []).map((r) =>
          r.student_id !== studentId ? r : ({
            ...r,
            picks: (r.picks || []).map((pp) =>
              pp.id === pick.id ? { ...pp, starred: !nextVal } : pp
            ),
          })
        ),
      }));
      toast.error(e?.response?.data?.detail || "Could not update star");
    }
  };

  const inviteToFastMatch = async (student) => {
    try {
      const r = await api.post(
        `/counsellors/cohorts/${cohortId}/students/${student.id}/invite-fast-match`,
        {}, inviteHeaders,
      );
      const outcome = r.data?.outcome;
      if (outcome === "created") {
        toast.success(`Invite sent to ${student.name}`);
      } else if (outcome === "skipped_duplicate") {
        toast.message(`${student.name} already has a live invite — use Resend to send again`);
      }
      // Refresh rollup so the row chip updates without a full page reload.
      const inv = await api.get(`/counsellors/cohorts/${cohortId}/invite-fast-match`)
        .catch(() => ({ data: { by_student: {} } }));
      setInviteStatus(inv.data?.by_student || {});
    } catch (e) {
      const d = e?.response?.data?.detail || e?.response?.data || {};
      const code = d?.code;
      if (code === "skipped_missing_email") {
        toast.error("Add an email to this student's roster row before inviting");
      } else if (code === "email_sender_not_configured") {
        toast.error("Email sending is not configured on this environment. Ask an admin to set a verified WUA sender.");
      } else if (code === "rate_limited") {
        toast.error("You've hit the invite rate limit — try again in a minute");
      } else if (code === "delivery_failed") {
        toast.error("Email provider refused this address");
      } else {
        toast.error(d?.message || "Could not send invite");
      }
    }
  };

  const resendInvite = async (student) => {
    try {
      await api.post(
        `/counsellors/cohorts/${cohortId}/students/${student.id}/invite-fast-match/resend`,
        {}, inviteHeaders,
      );
      toast.success(`Invite re-sent to ${student.name}`);
      const inv = await api.get(`/counsellors/cohorts/${cohortId}/invite-fast-match`)
        .catch(() => ({ data: { by_student: {} } }));
      setInviteStatus(inv.data?.by_student || {});
    } catch (e) {
      const d = e?.response?.data?.detail || e?.response?.data || {};
      toast.error(d?.message || "Could not resend");
    }
  };

  const cancelInvite = async (student) => {
    try {
      await api.post(
        `/counsellors/cohorts/${cohortId}/students/${student.id}/invite-fast-match/cancel`,
        {}, inviteHeaders,
      );
      toast.success(`Invite for ${student.name} cancelled`);
      const inv = await api.get(`/counsellors/cohorts/${cohortId}/invite-fast-match`)
        .catch(() => ({ data: { by_student: {} } }));
      setInviteStatus(inv.data?.by_student || {});
    } catch (e) {
      const d = e?.response?.data?.detail || e?.response?.data || {};
      toast.error(d?.message || "Could not cancel");
    }
  };

  const genShortlist = async (studentId) => {
    setBusy(true);
    try {
      const r = await api.post(`/counsellors/cohorts/${cohortId}/students/${studentId}/auto-shortlist?top_n=6`);
      toast.success(`Generated ${r.data.added} matched courses`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate shortlist");
    } finally {
      setBusy(false);
    }
  };

  const genForAll = async () => {
    setBusy(true);
    try {
      for (const s of students) {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/counsellors/cohorts/${cohortId}/students/${s.id}/auto-shortlist?top_n=6`);
      }
      toast.success(`Generated shortlists for ${students.length} students`);
      await load();
    } catch (e) {
      toast.error("Batch failed — some shortlists may have generated.");
    } finally {
      setBusy(false);
    }
  };

  const removeStudent = async (studentId) => {
    if (!confirm("Delete this student and their shortlist?")) return;
    await api.delete(`/counsellors/cohorts/${cohortId}/students/${studentId}`);
    onStudentsChanged();
    await load();
  };

  const clearShortlist = async (studentId, studentName) => {
    if (!confirm(`Clear all matches for ${studentName}? You can regenerate afterwards.`)) return;
    await api.delete(`/counsellors/cohorts/${cohortId}/students/${studentId}/shortlist`);
    toast.success(`Cleared ${studentName}'s shortlist`);
    await load();
  };

  if (!cohort) {
    return <div className="p-10 text-sm text-stone-500">Loading cohort…</div>;
  }

  return (
    <div className="flex-1 py-6 pl-8" data-testid="cohort-detail">
      {sharedResult && (
        <SharedResultModal
          info={sharedResult}
          busy={sharedBusy}
          onClose={() => setSharedResult(null)}
        />
      )}
      {/* Cohort header */}
      <div className="flex items-start justify-between gap-4 border-b border-stone-200 pb-4">
        <div>
          <div className="label-uppercase text-stone-500">{cohort.academic_year}</div>
          <h1 className="font-editorial text-3xl mt-1">{cohort.name}</h1>
          <div className="text-xs text-stone-500 mt-1">
            {students.length} student{students.length === 1 ? "" : "s"} · cohort id <span className="font-mono">{cohort.id}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <AddStudentDialog cohortId={cohortId} onAdded={() => { onStudentsChanged(); load(); }} atCap={false} />
          <BulkImportDialog cohortId={cohortId} onImported={() => { onStudentsChanged(); load(); }} />
          <Button
            variant="outline"
            size="sm"
            onClick={genForAll}
            disabled={busy || students.length === 0}
            data-testid="cohort-generate-all"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Generate all shortlists
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCsvExport(cohortId)}
            disabled={students.length === 0}
            data-testid="cohort-csv-export"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {students.length === 0 ? (
        <div className="mt-10 text-center border border-dashed border-stone-300 rounded-lg py-16 px-4">
          <Users className="w-6 h-6 mx-auto text-stone-400" />
          <div className="font-editorial text-xl mt-3">No students yet</div>
          <p className="text-sm text-stone-600 mt-2 max-w-md mx-auto">
            Add your first student — WUA will generate their shortlist in one click.
          </p>
        </div>
      ) : (
        <>
          <CohortInsightsPanel cohortId={cohortId} />
          <DeadlineTrackerPanel cohortId={cohortId} />
          {/* Class comparison table */}
          <section className="mt-8" data-testid="class-comparison">
            <div className="flex items-baseline justify-between">
              <div className="label-uppercase text-stone-500">Class comparison · top 6 per student</div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleDensity}
                  data-testid="toggle-density"
                  title="Switch between comfortable spacing and compact rows to fit more students on screen"
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    compact
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {compact ? "Compact" : "Comfortable"}
                </button>
                <button
                  type="button"
                  onClick={() => setSortByEngagement((v) => !v)}
                  data-testid="toggle-sort-engagement"
                  title="Sort students and their picks by how many times they've clicked through from the share link"
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    sortByEngagement
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {sortByEngagement ? "Sorted by engagement ↓" : "Sort by engagement"}
                </button>
                <span className="text-[11px] text-stone-500">Undergraduate courses · UK-only preview data</span>
              </div>
            </div>
            <div className="mt-3 overflow-x-auto border border-stone-200 rounded-lg bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-stone-50 text-xs text-stone-500 uppercase tracking-wider">
                  <tr>
                    <th className={`text-left ${cellCls}`}>Student</th>
                    <th className={`text-left ${cellCls}`}>Grades</th>
                    <th className={`text-left ${cellCls}`}>Rank</th>
                    <th className={`text-left ${cellCls}`}>University</th>
                    <th className={`text-left ${cellCls}`}>Course</th>
                    <th className={`text-left ${cellCls}`}>Realism · Reasons</th>
                    <th className={`text-left ${cellCls}`}>5-yr salary</th>
                    <th className={`text-right ${cellCls}`}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {displayedStudents.map((s) => {
                    const cmpRow = comparison.results.find((r) => r.student_id === s.id);
                    const rawPicks = cmpRow?.picks || [];
                    const picks = sortByEngagement ? sortPicksByEngagement(s.id, rawPicks) : rawPicks;
                    const isFallback = cmpRow?.is_fallback_match;
                    if (picks.length === 0) {
                      return (
                        <tr key={s.id} data-testid={`cmp-row-${s.id}`}>
                          <td className={`${cellCls} font-medium`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span>{s.name}</span>
                              <FastMatchPill
                                invite={inviteStatus[s.id]}
                                studentId={s.id}
                                onViewShared={openSharedResult}
                              />
                            </div>
                          </td>
                          <td className={`${cellCls} font-mono text-xs`}>{s.predicted_grades || "—"}</td>
                          <td colSpan={5} className={`${cellCls} text-stone-500 italic`}>
                            No shortlist yet
                          </td>
                          <td className={`${compact ? "px-1.5 py-1.5" : "px-2 py-3"} text-right whitespace-nowrap`}>
                            <div className="inline-flex items-center gap-0.5">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => genShortlist(s.id)}
                                disabled={busy}
                                data-testid={`cmp-generate-${s.id}`}
                                className="h-7 px-2 text-xs"
                              >
                                <Sparkles className="w-3 h-3 mr-1" /> Match
                              </Button>
                              <ManualAddCourseDialog
                                cohortId={cohortId}
                                student={s}
                                onAdded={load}
                                trigger={(
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0"
                                    data-testid={`cmp-manual-add-${s.id}`}
                                    title="Add a course manually"
                                  >
                                    <BookPlus className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              />
                              <InsightsShareDialog
                                cohortId={cohortId}
                                student={s}
                                onChanged={load}
                                trigger={(
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0"
                                    data-testid={`cmp-insights-${s.id}`}
                                    title="Insights & share"
                                  >
                                    <Share2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              />
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`cmp-more-${s.id}`}
                                    title="More actions"
                                    className="h-7 w-7 p-0 text-stone-500"
                                  >
                                    <MoreHorizontal className="w-3.5 h-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-44">
                                  <StudentDialog
                                    cohortId={cohortId}
                                    mode="edit"
                                    initial={s}
                                    onDone={() => { onStudentsChanged(); load(); }}
                                    trigger={(
                                      <DropdownMenuItem
                                        onSelect={(e) => e.preventDefault()}
                                        className="text-xs"
                                        data-testid={`cmp-edit-${s.id}`}
                                      >
                                        <Pencil className="w-3.5 h-3.5 mr-2" /> Edit student
                                      </DropdownMenuItem>
                                    )}
                                  />
                                  {/* Fast Match invite — three-state UI. Availability is
                                      driven entirely by the counsellor's OWN invites for
                                      this student and is independent of course matches. */}
                                  {!inviteStatus[s.id] && (
                                    <DropdownMenuItem
                                      className="text-xs"
                                      onSelect={() => inviteToFastMatch(s)}
                                      data-testid={`fm-invite-${s.id}`}
                                    >
                                      <Send className="w-3.5 h-3.5 mr-2" /> Invite to Fast Match
                                    </DropdownMenuItem>
                                  )}
                                  {inviteStatus[s.id]?.status === "invited" && (
                                    <>
                                      <DropdownMenuItem
                                        className="text-xs"
                                        onSelect={() => resendInvite(s)}
                                        data-testid={`fm-resend-${s.id}`}
                                      >
                                        <RefreshCw className="w-3.5 h-3.5 mr-2" /> Resend Fast Match invite
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="text-xs text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                                        onSelect={() => cancelInvite(s)}
                                        data-testid={`fm-cancel-${s.id}`}
                                      >
                                        <XCircle className="w-3.5 h-3.5 mr-2" /> Cancel Fast Match invite
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {inviteStatus[s.id] && ["completed","expired","cancelled","delivery_failed"].includes(inviteStatus[s.id].status) && (
                                    <DropdownMenuItem
                                      className="text-xs"
                                      onSelect={() => inviteToFastMatch(s)}
                                      data-testid={`fm-reinvite-${s.id}`}
                                    >
                                      <Send className="w-3.5 h-3.5 mr-2" /> Invite to Fast Match again
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-xs text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                                    onSelect={() => removeStudent(s.id)}
                                    data-testid={`cmp-remove-${s.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete student
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    return picks.map((p, i) => (
                      <tr key={`${s.id}-${i}`} data-testid={`cmp-row-${s.id}-pick-${i}`}>
                        {i === 0 && (
                          <>
                            <td rowSpan={picks.length} className={`${cellClsFirst} font-medium`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span>{s.name}</span>
                                <FastMatchPill
                                  invite={inviteStatus[s.id]}
                                  studentId={s.id}
                                  onViewShared={openSharedResult}
                                />
                              </div>
                              {studentTotalClicks(s.id) > 0 && (
                                <div
                                  className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-mono"
                                  title="Total clicks this student has made across all shortlisted courses from the share link"
                                  data-testid={`cmp-student-total-clicks-${s.id}`}
                                >
                                  {studentTotalClicks(s.id)} total click{studentTotalClicks(s.id) === 1 ? "" : "s"}
                                </div>
                              )}
                              {studentTotalClicks(s.id) === 0 && s.share_enabled && s.share_token && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const url = `${window.location.origin}/s/${s.share_token}`;
                                    try {
                                      await navigator.clipboard.writeText(url);
                                      toast.success("Share link copied — nudge them by email or WhatsApp");
                                    } catch {
                                      toast.info(url);
                                    }
                                  }}
                                  className="mt-1 block text-[10px] text-stone-500 italic hover:text-stone-800 underline decoration-dotted underline-offset-2"
                                  title="Student hasn't opened any recommendations yet. Click to copy their share link and follow up."
                                  data-testid={`cmp-zero-click-nudge-${s.id}`}
                                >
                                  no clicks yet — resend link?
                                </button>
                              )}
                              {isFallback && (
                                <div
                                  className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                                  title="Broad match — no subjects listed. Add 1-2 subjects for sharper results."
                                  data-testid={`cmp-fallback-warn-${s.id}`}
                                >
                                  <AlertTriangle className="w-2.5 h-2.5" />
                                  broad match
                                </div>
                              )}
                            </td>
                            <td rowSpan={picks.length} className={`${cellClsFirst} font-mono text-xs`}>
                              {s.predicted_grades || "—"}
                            </td>
                          </>
                        )}
                        <td className={rankCls}>
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => toggleStar(s.id, p)}
                              className={
                                "shrink-0 rounded p-0.5 transition-colors " +
                                (p.starred
                                  ? "text-amber-500 hover:text-amber-600"
                                  : "text-stone-300 hover:text-amber-500")
                              }
                              title={p.starred ? "Unstar this pick" : "Star as top recommendation"}
                              data-testid={`cmp-star-${s.id}-${i}`}
                              aria-label={p.starred ? "Unstar" : "Star"}
                            >
                              <Star
                                className="w-3.5 h-3.5"
                                fill={p.starred ? "currentColor" : "none"}
                              />
                            </button>
                            <span>#{i + 1}</span>
                          </div>
                        </td>
                        <td className={cellCls}>
                          {p.uni_id ? (
                            <Link to={`/uni/${p.uni_id}`} className="hover:underline underline-offset-4 decoration-stone-400">
                              {p.uni_name || "—"}
                            </Link>
                          ) : (
                            <span>{p.uni_name || "—"}</span>
                          )}
                          <div className="text-[10px] text-stone-500 font-mono">{p.country}</div>
                        </td>
                        <td className={cellCls}>
                          {p.degree_id ? (
                            <div className="flex items-center gap-2">
                              <CoursePreviewHover degreeId={p.degree_id}>
                                <Link
                                  to={`/degree/${p.degree_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline underline-offset-4 decoration-stone-400"
                                  data-testid={`cmp-degree-link-${s.id}-${i}`}
                                >
                                  {p.degree_name || "—"}
                                </Link>
                              </CoursePreviewHover>
                              {clicks?.[s.id]?.[p.degree_id]?.count > 0 && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-mono"
                                  title={`Student opened this ${clicks[s.id][p.degree_id].count} time(s). Last: ${clicks[s.id][p.degree_id].last?.slice(0,10) || "—"}`}
                                  data-testid={`cmp-clicks-${s.id}-${i}`}
                                >
                                  {clicks[s.id][p.degree_id].count} click{clicks[s.id][p.degree_id].count === 1 ? "" : "s"}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span>{p.degree_name || "—"}</span>
                          )}
                        </td>
                        <td className={`${cellCls} min-w-[220px]`}>
                          <RealismChip realism={p.entry_realism} />
                          {(p.reasons || []).length > 0 && (
                            <ul className={`${compact ? "mt-1" : "mt-1.5"} space-y-0.5 text-[11px] text-stone-600 leading-tight`}>
                              {p.reasons.slice(0, reasonLimit).map((r, ri) => (
                                <li key={ri} className="flex items-start gap-1">
                                  <span className="text-stone-400 mt-[3px]">·</span>
                                  <span>{r}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className={`${cellCls} font-mono text-xs whitespace-nowrap`}>
                          {p.leo_median_5yr_gbp ? `£${p.leo_median_5yr_gbp.toLocaleString()}` : "—"}
                        </td>
                        {i === 0 && (
                          <td rowSpan={picks.length} className={`${compact ? "px-1.5 py-1.5" : "px-2 py-3"} text-right align-top border-l border-stone-100 whitespace-nowrap`}>
                            <div className="inline-flex items-center gap-0.5">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => genShortlist(s.id)}
                                disabled={busy}
                                data-testid={`cmp-regenerate-${s.id}`}
                                title="Regenerate matches"
                                className="h-7 w-7 p-0"
                              >
                                <Sparkles className="w-3.5 h-3.5" />
                              </Button>
                              <ManualAddCourseDialog
                                cohortId={cohortId}
                                student={s}
                                onAdded={load}
                                trigger={(
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`cmp-manual-add-${s.id}`}
                                    title="Add a course manually"
                                    className="h-7 w-7 p-0"
                                  >
                                    <BookPlus className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              />
                              <InsightsShareDialog
                                cohortId={cohortId}
                                student={s}
                                onChanged={load}
                                trigger={(
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`cmp-insights-${s.id}`}
                                    title="Insights & share"
                                    className="h-7 w-7 p-0"
                                  >
                                    <Share2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              />
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`cmp-more-${s.id}`}
                                    title="More actions"
                                    className="h-7 w-7 p-0 text-stone-500"
                                  >
                                    <MoreHorizontal className="w-3.5 h-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-44">
                                  <StudentDialog
                                    cohortId={cohortId}
                                    mode="edit"
                                    initial={s}
                                    onDone={() => { onStudentsChanged(); load(); }}
                                    trigger={(
                                      <DropdownMenuItem
                                        onSelect={(e) => e.preventDefault()}
                                        className="text-xs"
                                        data-testid={`cmp-edit-${s.id}`}
                                      >
                                        <Pencil className="w-3.5 h-3.5 mr-2" /> Edit student
                                      </DropdownMenuItem>
                                    )}
                                  />
                                  <DropdownMenuItem
                                    className="text-xs"
                                    onSelect={() => clearShortlist(s.id, s.name)}
                                    disabled={busy}
                                    data-testid={`cmp-clear-${s.id}`}
                                  >
                                    <Eraser className="w-3.5 h-3.5 mr-2" /> Clear all matches
                                  </DropdownMenuItem>
                                  <ReferenceLetterDialog
                                    cohortId={cohortId}
                                    student={s}
                                    hasShortlist={picks.length > 0}
                                    trigger={(
                                      <DropdownMenuItem
                                        onSelect={(e) => e.preventDefault()}
                                        className="text-xs"
                                        data-testid={`cmp-reference-${s.id}`}
                                      >
                                        <FileText className="w-3.5 h-3.5 mr-2" /> Draft reference stub
                                      </DropdownMenuItem>
                                    )}
                                  />
                                  {/* Fast Match invite — three-state UI. Availability is
                                      driven entirely by the counsellor's OWN invites for
                                      this student (rollup endpoint is per-counsellor). */}
                                  {!inviteStatus[s.id] && (
                                    <DropdownMenuItem
                                      className="text-xs"
                                      onSelect={() => inviteToFastMatch(s)}
                                      data-testid={`fm-invite-${s.id}`}
                                    >
                                      <Send className="w-3.5 h-3.5 mr-2" /> Invite to Fast Match
                                    </DropdownMenuItem>
                                  )}
                                  {inviteStatus[s.id]?.status === "invited" && (
                                    <>
                                      <DropdownMenuItem
                                        className="text-xs"
                                        onSelect={() => resendInvite(s)}
                                        data-testid={`fm-resend-${s.id}`}
                                      >
                                        <RefreshCw className="w-3.5 h-3.5 mr-2" /> Resend Fast Match invite
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="text-xs text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                                        onSelect={() => cancelInvite(s)}
                                        data-testid={`fm-cancel-${s.id}`}
                                      >
                                        <XCircle className="w-3.5 h-3.5 mr-2" /> Cancel Fast Match invite
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {inviteStatus[s.id] && ["completed","expired","cancelled","delivery_failed"].includes(inviteStatus[s.id].status) && (
                                    <DropdownMenuItem
                                      className="text-xs"
                                      onSelect={() => inviteToFastMatch(s)}
                                      data-testid={`fm-reinvite-${s.id}`}
                                    >
                                      <Send className="w-3.5 h-3.5 mr-2" /> Invite to Fast Match again
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-xs text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                                    onSelect={() => removeStudent(s.id)}
                                    data-testid={`cmp-remove-${s.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete student
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        )}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}


function RealismChip({ realism }) {
  const s = REALISM_STYLE[realism] || REALISM_STYLE.unknown;
  return (
    <span
      data-testid={`realism-${realism || "unknown"}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${s.chip}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}


// ---------------------------------------------------------------------------
// Page wrapper — handles auth + counsellor gate + cohort selection
// ---------------------------------------------------------------------------


export default function CounsellorsDashboard() {
  const [me, setMe] = useState(null);
  const [cohortData, setCohortData] = useState({ results: [], students_used: 0, student_cap: 15 });
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);

  const loadMe = async () => {
    try {
      const r = await api.get("/counsellors/me");
      setMe(r.data);
      return r.data;
    } catch (e) {
      if (e?.response?.status === 401) setAuthError(true);
      return null;
    }
  };

  const loadCohorts = async () => {
    try {
      const r = await api.get("/counsellors/cohorts");
      setCohortData(r.data);
      if (!activeId && r.data.results?.length) setActiveId(r.data.results[0].id);
    } catch (e) {
      if (e?.response?.status === 401) setAuthError(true);
    }
  };

  useEffect(() => {
    (async () => {
      const meRes = await loadMe();
      if (meRes?.is_counsellor) await loadCohorts();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createCohort = async (name) => {
    try {
      const r = await api.post("/counsellors/cohorts", { name, academic_year: "2026-27" });
      toast.success("Cohort created");
      await loadCohorts();
      setActiveId(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create cohort");
    }
  };

  const deleteCohort = async (id) => {
    await api.delete(`/counsellors/cohorts/${id}`);
    toast.success("Cohort deleted");
    if (activeId === id) setActiveId(null);
    await loadCohorts();
  };

  const csvExport = async (cohortId) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    // The endpoint sets Content-Disposition — just open it in a new tab so
    // the browser handles the file download with the session cookie.
    window.open(`${backend}/api/counsellors/cohorts/${cohortId}/comparison.csv`, "_blank");
  };

  // ---- render states -----------------------------------------------------

  if (loading) {
    return <div className="max-w-4xl mx-auto p-12 text-sm text-stone-500">Loading counsellor workspace…</div>;
  }

  if (authError || !me) {
    return (
      <div className="max-w-2xl mx-auto p-12 text-center" data-testid="counsellors-auth-required">
        <AlertTriangle className="w-6 h-6 mx-auto text-stone-500" />
        <h1 className="font-editorial text-3xl mt-3">Sign in to open the Counsellor Dashboard</h1>
        <p className="text-sm text-stone-600 mt-2">
          The Counsellor Tools require a signed-in account. Sign in from the top bar.
        </p>
        <Link to="/counsellors" className="mt-6 inline-block text-sm underline underline-offset-4 decoration-stone-400">
          Back to Counsellor overview
        </Link>
      </div>
    );
  }

  if (!me.is_counsellor) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <SEOHead title="Counsellor Dashboard · WhichUniversityAdvisor" description="Cohort manager for counsellors." path="/counsellors/dashboard" />
        <SelfDeclareCard onDone={async () => { await loadMe(); await loadCohorts(); }} />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <SEOHead title={`Counsellor Dashboard · ${me.school_name}`} description="Manage cohorts, generate shortlists, export class comparison." path="/counsellors/dashboard" />

      <div className="pt-6 pb-3 flex items-center justify-between border-b border-stone-200">
        <div>
          <div className="label-uppercase text-stone-500">Counsellor workspace</div>
          <h1 className="font-editorial text-2xl mt-1" data-testid="counsellors-school-name">{me.school_name}</h1>
        </div>
        <div className="text-xs text-stone-500 flex items-center gap-3">
          <Badge variant="outline">{me.plan === "pro" ? "Pro" : "Free"}</Badge>
          <span className="font-mono" data-testid="counsellors-usage">
            {cohortData.students_used}/{cohortData.student_cap} students used
          </span>
        </div>
      </div>

      {cohortData.students_used >= cohortData.student_cap && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>You've hit the free-tier cap of {cohortData.student_cap} students. Contact us for a school licence.</span>
        </div>
      )}

      <div className="mt-4">
        <DigestSettingsCard me={me} onMeChanged={setMe} />
      </div>

      <div className="mt-4">
        <SchoolAccessPanel me={me} onMeChanged={setMe} />
      </div>

      <div className="flex min-h-[70vh]">
        <CohortRail
          cohorts={cohortData.results || []}
          activeId={activeId}
          onSelect={setActiveId}
          onCreate={createCohort}
          onDelete={deleteCohort}
          cap={cohortData.student_cap}
          used={cohortData.students_used}
        />
        {activeId ? (
          <CohortDetail
            cohortId={activeId}
            onStudentsChanged={loadCohorts}
            onCsvExport={csvExport}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-stone-500 text-sm" data-testid="cohort-empty-state">
            <div className="text-center">
              <ChevronRight className="w-6 h-6 mx-auto text-stone-400" />
              <p className="mt-2">Create or select a cohort on the left to get started.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Reference-Letter Stub Dialog
// Pulls the student's own shortlist + admission-probability signals,
// asks Claude for ONE factual paragraph, and hands it to the teacher
// with a copy-to-clipboard button. The teacher adds personal voice.
// ─────────────────────────────────────────────────────────────────────
function ReferenceLetterDialog({ cohortId, student, hasShortlist, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [tone, setTone] = useState("ucas");

  const _TONES = [
    { id: "ucas",          label: "UCAS · UK",           hint: "Formal-neutral · British English · surname after first mention" },
    { id: "common_app",    label: "Common App · US",     hint: "Warmer · first name · holistic fit language" },
    { id: "warm_specific", label: "Warm & specific",     hint: "Plain-English · what they'd learn & who they'd be around" },
  ];

  const generate = async (chosenTone) => {
    const t = chosenTone || tone;
    setBusy(true);
    try {
      const r = await api.post(
        `/counsellors/cohorts/${cohortId}/students/${student.id}/reference-letter-stub?tone=${t}`,
      );
      setDraft(r.data);
      setTone(t);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally {
      setBusy(false);
    }
  };

  const copyStub = async () => {
    if (!draft?.stub) return;
    await navigator.clipboard.writeText(draft.stub);
    toast.success("Copied — paste into your reference and add your personal voice");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { setOpen(v); if (!v) setDraft(null); }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="max-w-2xl"
        data-testid="reference-letter-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl">
            Reference stub · {student.name || "student"}
          </DialogTitle>
        </DialogHeader>
        {!hasShortlist ? (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
            This student has no shortlist yet. Click <b>Match</b> on their row
            first — the stub weaves together their shortlist picks with
            their predicted grades.
          </div>
        ) : !draft ? (
          <div className="space-y-4">
            <p className="text-sm text-stone-600 leading-relaxed">
              We'll draft one plain-prose paragraph a teacher can drop into a
              reference letter. It weaves the student's predicted grades, the
              intellectual thread across their top shortlist choices, and the
              reach/match/safe split — <b>strictly</b> from what you've
              entered. No invented character claims. You add personal voice.
            </p>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-2">
                Tone preset
              </div>
              <div className="flex flex-col gap-2">
                {_TONES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTone(t.id)}
                    data-testid={`reference-tone-${t.id}`}
                    className={`text-left p-3 rounded border transition ${
                      tone === t.id
                        ? "bg-indigo-50 border-indigo-400 ring-1 ring-indigo-300"
                        : "bg-white border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    <div className="font-semibold text-sm">{t.label}</div>
                    <div className="text-[11px] text-stone-500">{t.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => generate()}
                disabled={busy}
                data-testid="reference-letter-generate"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                {busy ? "Drafting…" : "Draft factual paragraph"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-widest text-indigo-700">
                Tone · {_TONES.find((x) => x.id === draft.tone)?.label || draft.tone}
              </div>
              <div className="flex gap-1">
                {_TONES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => generate(t.id)}
                    disabled={busy}
                    data-testid={`reference-retone-${t.id}`}
                    className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-mono transition ${
                      draft.tone === t.id
                        ? "bg-indigo-600 text-white"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {t.label.split(" · ")[0]}
                  </button>
                ))}
              </div>
            </div>
            <div
              className="p-4 bg-stone-50 border border-stone-200 rounded font-serif text-[15px] leading-relaxed whitespace-pre-wrap"
              data-testid="reference-letter-stub-text"
            >
              {draft.stub}
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-emerald-900">
                <div className="font-underground-sc tracking-wider text-[9px] text-emerald-700">Safe</div>
                <div className="text-lg font-semibold mt-0.5">{draft.based_on?.realism_split?.safe ?? 0}</div>
              </div>
              <div className="p-2 bg-indigo-50 border border-indigo-200 rounded text-indigo-900">
                <div className="font-underground-sc tracking-wider text-[9px] text-indigo-700">Match</div>
                <div className="text-lg font-semibold mt-0.5">{draft.based_on?.realism_split?.match ?? 0}</div>
              </div>
              <div className="p-2 bg-rose-50 border border-rose-200 rounded text-rose-900">
                <div className="font-underground-sc tracking-wider text-[9px] text-rose-700">Reach</div>
                <div className="text-lg font-semibold mt-0.5">{draft.based_on?.realism_split?.reach ?? 0}</div>
              </div>
            </div>
            <p className="text-[11px] text-stone-500 italic leading-relaxed">
              {draft.disclaimer}
            </p>
            <DialogFooter className="gap-2">
              <Button
                size="sm"
                onClick={copyStub}
                data-testid="reference-letter-copy"
              >
                Copy paragraph
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

